import { z } from "zod";

/**
 * A match replay: what the demo viewer plays back, read from a CS2 demo in the
 * uploader's browser and stored instead of the demo (a few MB against hundreds).
 *
 * Bytes: "69RP", the format version (u32), the header length (u32), the header
 * as JSON, then each column's bytes in the header's order, each one aligned to 4.
 * The whole is gzipped for storage and transfer.
 *
 * Frames are samples of the match (16 a second by default) during the kept rounds.
 * Player columns hold `frames × players` values: player `p` of frame `f` is at
 * `f * players.length + p`. A player who is not in the game at a frame has
 * flags 0.
 */
export const REPLAY_MAGIC = "69RP";
/** 2 (0.12.0): grenades carry the exact spot and view of their release (`from`). Version 1 replays still open. */
export const REPLAY_FORMAT = 3;
export const REPLAY_FORMATS_READ = [1, 2, 3] as const;

/** Players flags, one byte per player per frame. */
export const PF = {
  present: 1,
  alive: 2,
  ct: 4,
  defusing: 8,
  scoped: 16,
  kit: 32,
  bomb: 64,
  helmet: 128,
} as const;

/** Where the bomb is, per frame. */
export const BOMB_STATE = { none: 0, carried: 1, dropped: 2, planted: 3 } as const;

export const REPLAY_GRENADES = ["smoke", "flash", "molotov", "he", "decoy"] as const;
export type ReplayGrenadeKind = (typeof REPLAY_GRENADES)[number];

const Index = z.number().int().min(-1).max(63);
const Tick = z.number().int().min(0);
const Name = z.string().max(64);

export const ReplayPlayer = z.object({
  steamId: z.string().max(32),
  name: Name,
  /** Which of the two teams: 0 started the match as T. */
  team: z.union([z.literal(0), z.literal(1)]),
});
export type ReplayPlayer = z.infer<typeof ReplayPlayer>;

export const ReplayTeam = z.object({
  /** The clan name when the demo has one, else null (the viewer names it after a player). */
  name: z.string().max(64).nullable(),
  score: z.number().int().min(0).max(99),
});
export type ReplayTeam = z.infer<typeof ReplayTeam>;

export const ReplayRound = z.object({
  number: z.number().int().min(1).max(99),
  /** Freeze time starts. */
  startTick: Tick,
  /** Freeze time ends: the round clock starts at 1:55. */
  liveTick: Tick,
  /** The round is decided. */
  endTick: Tick,
  /** Last tick of the round's window, before the next freeze time. */
  postTick: Tick,
  winner: z.enum(["t", "ct"]).nullable(),
  reason: z.string().max(40).nullable(),
  /** Which team (0 or 1) plays T this round. */
  tTeam: z.union([z.literal(0), z.literal(1)]),
  /** Both teams' scores once this round is over. */
  score: z.tuple([z.number().int().min(0), z.number().int().min(0)]),
  /** Its frames: [frameFrom, frameTo). */
  frameFrom: z.number().int().min(0),
  frameTo: z.number().int().min(0),
});
export type ReplayRound = z.infer<typeof ReplayRound>;

export const ReplayKill = z.object({
  tick: Tick,
  killer: Index,
  victim: Index,
  assister: Index,
  weapon: z.string().max(40),
  headshot: z.boolean(),
  wallbang: z.boolean(),
  smoke: z.boolean(),
  noScope: z.boolean(),
  blind: z.boolean(),
  flashAssist: z.boolean(),
  /** The killer was in the air (format 3). */
  air: z.boolean().optional(),
  /** How far apart, in metres, as the game counts (format 3). */
  distance: z.number().min(0).max(1000).optional(),
});
export type ReplayKill = z.infer<typeof ReplayKill>;

/**
 * What a player carries, each time it changes (format 3): [tick, player, ...weapons],
 * weapons as indexes into `weapons`, a grenade once per one carried.
 */
export const ReplayInventory = z.array(z.number().int().min(0)).min(2).max(24);
export type ReplayInventory = z.infer<typeof ReplayInventory>;

/** A player's money in a round (format 3), read when the round ends. */
export const ReplayEconomy = z.object({
  /** Index into `rounds`. */
  round: z.number().int().min(0).max(79),
  player: z.number().int().min(0).max(63),
  /** Money at the start of the round. */
  start: z.number().int().min(0).max(65535),
  /** Spent in the round. */
  spent: z.number().int().min(0).max(65535),
  /** What they carried once the buy time was over (bought and picked up). */
  value: z.number().int().min(0).max(65535),
  /** What they bought, as indexes into `weapons` ("kevlar", "assaultsuit", "defuser" included). */
  buys: z.array(z.number().int().min(0).max(255)).max(24),
});
export type ReplayEconomy = z.infer<typeof ReplayEconomy>;

/** [tick, attacker, victim, health damage, weapon index] */
export const ReplayHurt = z.tuple([Tick, Index, Index, z.number().int().min(0).max(500), z.number().int().min(0)]);
export type ReplayHurt = z.infer<typeof ReplayHurt>;

/** [tick, flasher, victim, seconds × 100] */
export const ReplayBlind = z.tuple([Tick, Index, Index, z.number().int().min(0).max(10_000)]);
export type ReplayBlind = z.infer<typeof ReplayBlind>;

export const ReplayGrenade = z.object({
  kind: z.enum(REPLAY_GRENADES),
  thrower: Index,
  throwTick: Tick,
  /** Where it went off (smoke popped, flash or HE exploded, fire started), and when. */
  detonateTick: Tick.nullable(),
  at: z.tuple([z.number(), z.number(), z.number()]).nullable(),
  /** When its effect ended (smoke faded, fire out); the detonation for a flash or an HE. */
  effectEndTick: Tick.nullable(),
  /** Flight: x, y, z one sample apart, from the throw to the detonation. */
  path: z.array(z.number().int()).max(3 * 2000),
  /**
   * Where the thrower stood and looked when they let go (x, y, z of their feet, pitch,
   * yaw), at the exact tick: what a lineup is. From format 2; null when unknown.
   */
  from: z.tuple([z.number(), z.number(), z.number(), z.number(), z.number()]).nullable().optional(),
});
export type ReplayGrenade = z.infer<typeof ReplayGrenade>;

export const ReplayBombEvent = z.object({
  type: z.enum(["plant_start", "planted", "defuse_start", "defused", "exploded"]),
  tick: Tick,
  player: Index,
  /** A or B when the demo says (from the site's entity). */
  site: z.string().max(4).nullable(),
});
export type ReplayBombEvent = z.infer<typeof ReplayBombEvent>;

export const ReplayChat = z.object({ tick: Tick, player: Index, text: z.string().max(200) });
export type ReplayChat = z.infer<typeof ReplayChat>;

export const COLUMN_TYPES = ["Int32Array", "Int16Array", "Uint16Array", "Uint8Array", "Int8Array"] as const;
type ColumnType = (typeof COLUMN_TYPES)[number];

/** The columns, in order, with their type and whether they hold one value per player. */
export const REPLAY_COLUMNS = {
  frameTicks: { type: "Int32Array", perPlayer: false },
  x: { type: "Int16Array", perPlayer: true },
  y: { type: "Int16Array", perPlayer: true },
  z: { type: "Int16Array", perPlayer: true },
  /** View yaw, 0–255 for 0–360°. */
  yaw: { type: "Uint8Array", perPlayer: true },
  pitch: { type: "Int8Array", perPlayer: true },
  hp: { type: "Uint8Array", perPlayer: true },
  armor: { type: "Uint8Array", perPlayer: true },
  money: { type: "Uint16Array", perPlayer: true },
  /** Index into `weapons`; 0 = none. */
  weapon: { type: "Uint8Array", perPlayer: true },
  flags: { type: "Uint8Array", perPlayer: true },
  bombX: { type: "Int16Array", perPlayer: false },
  bombY: { type: "Int16Array", perPlayer: false },
  bombZ: { type: "Int16Array", perPlayer: false },
  bombState: { type: "Uint8Array", perPlayer: false },
} as const satisfies Record<string, { type: ColumnType; perPlayer: boolean }>;
export type ReplayColumnName = keyof typeof REPLAY_COLUMNS;

type ArrayOf<T extends ColumnType> = T extends "Int32Array" ? Int32Array : T extends "Int16Array" ? Int16Array : T extends "Uint16Array" ? Uint16Array : T extends "Int8Array" ? Int8Array : Uint8Array;
export type ReplayColumns = { [K in ReplayColumnName]: ArrayOf<(typeof REPLAY_COLUMNS)[K]["type"]> };

export const ReplayHeader = z.object({
  format: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  map: z.string().max(40),
  tickRate: z.number().int().min(16).max(256),
  /** Frames a second. */
  sampleRate: z.number().int().min(1).max(64),
  server: z.string().max(200).nullable(),
  /** Ticks of the whole demo (its length). */
  demoTicks: Tick,
  /** Which tool and version wrote it. */
  writer: z.string().max(60),
  teams: z.tuple([ReplayTeam, ReplayTeam]),
  players: z.array(ReplayPlayer).min(1).max(24),
  rounds: z.array(ReplayRound).max(80),
  /** Weapon names; the weapon column holds indexes into it, 0 being "". */
  weapons: z.array(z.string().max(40)).max(255),
  kills: z.array(ReplayKill).max(2_000),
  hurts: z.array(ReplayHurt).max(20_000),
  blinds: z.array(ReplayBlind).max(5_000),
  grenades: z.array(ReplayGrenade).max(3_000),
  bomb: z.array(ReplayBombEvent).max(1_000),
  chat: z.array(ReplayChat).max(2_000),
  /** Shots: [tick, player] pairs, flat. */
  shots: z.array(z.number().int()).max(400_000),
  /** What each player carries, as it changes (format 3). */
  inventory: z.array(ReplayInventory).max(80_000).optional(),
  /** Money and buys per round and player (format 3). */
  economy: z.array(ReplayEconomy).max(4_000).optional(),
  columns: z.array(z.tuple([z.string(), z.enum(COLUMN_TYPES), z.number().int().min(0)])),
});
export type ReplayHeader = z.infer<typeof ReplayHeader>;

export type Replay = Omit<ReplayHeader, "columns"> & { frames: number; columns: ReplayColumns };

const ctor = { Int32Array, Int16Array, Uint16Array, Uint8Array, Int8Array } as const;
const align = (n: number) => (n + 3) & ~3;

/** The replay as bytes (not yet gzipped). */
export function encodeReplay(r: Replay): Uint8Array {
  const names = Object.keys(REPLAY_COLUMNS) as ReplayColumnName[];
  const header: ReplayHeader = {
    ...(({ frames: _f, columns: _c, ...rest }) => rest)(r),
    columns: names.map((n) => [n, REPLAY_COLUMNS[n].type, r.columns[n].length]),
  };
  const json = new TextEncoder().encode(JSON.stringify(header));
  let size = 12 + align(json.length);
  for (const n of names) size += align(r.columns[n].byteLength);
  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  out.set(new TextEncoder().encode(REPLAY_MAGIC), 0);
  view.setUint32(4, r.format, true);
  view.setUint32(8, json.length, true);
  out.set(json, 12);
  let at = 12 + align(json.length);
  for (const n of names) {
    const a = r.columns[n];
    out.set(new Uint8Array(a.buffer, a.byteOffset, a.byteLength), at);
    at += align(a.byteLength);
  }
  return out;
}

export class ReplayFormatError extends Error {}

/**
 * Reads replay bytes (already gunzipped) and checks them: magic, version, a
 * header that passes the schema, columns of the right types and lengths, rounds
 * and indexes inside the frames and players.
 */
export function decodeReplay(bytes: Uint8Array): Replay {
  if (bytes.length < 12 || new TextDecoder().decode(bytes.subarray(0, 4)) !== REPLAY_MAGIC) throw new ReplayFormatError("Not a 69LY replay.");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const format = view.getUint32(4, true);
  if (!(REPLAY_FORMATS_READ as readonly number[]).includes(format)) throw new ReplayFormatError(`Replay format ${format} is not supported.`);
  const length = view.getUint32(8, true);
  if (12 + length > bytes.length) throw new ReplayFormatError("The replay is cut short.");
  let raw: unknown;
  try {
    raw = JSON.parse(new TextDecoder().decode(bytes.subarray(12, 12 + length)));
  } catch {
    throw new ReplayFormatError("The replay's header is damaged.");
  }
  const parsed = ReplayHeader.safeParse(raw);
  if (!parsed.success) throw new ReplayFormatError(`The replay's header is not valid: ${parsed.error.issues[0]?.path.join(".")} ${parsed.error.issues[0]?.message}`);
  const header = parsed.data;
  const names = Object.keys(REPLAY_COLUMNS) as ReplayColumnName[];
  if (header.columns.length !== names.length || header.columns.some(([n, t], i) => n !== names[i] || t !== REPLAY_COLUMNS[names[i]].type))
    throw new ReplayFormatError("The replay's columns are not the expected ones.");
  const frames = header.columns[0][2];
  const players = header.players.length;
  const columns = {} as Record<ReplayColumnName, ArrayLike<number>>;
  let at = 12 + align(length);
  for (const [name, type, count] of header.columns) {
    const def = REPLAY_COLUMNS[name as ReplayColumnName];
    if (count !== (def.perPlayer ? frames * players : frames)) throw new ReplayFormatError(`Column ${name} has ${count} values.`);
    const C = ctor[type];
    const byteLength = count * C.BYTES_PER_ELEMENT;
    if (at + byteLength > bytes.length) throw new ReplayFormatError("The replay is cut short.");
    // Copy into an aligned buffer of its own: the source offset may not suit the type.
    const copy = new Uint8Array(byteLength);
    copy.set(bytes.subarray(at, at + byteLength));
    columns[name as ReplayColumnName] = new C(copy.buffer);
    at += align(byteLength);
  }
  for (const r of header.rounds) {
    if (r.frameFrom > r.frameTo || r.frameTo > frames) throw new ReplayFormatError(`Round ${r.number}'s frames are outside the replay.`);
  }
  const inRange = (i: number) => i >= -1 && i < players;
  if (!header.kills.every((k) => inRange(k.killer) && inRange(k.victim) && inRange(k.assister))) throw new ReplayFormatError("A kill names an unknown player.");
  if (!header.grenades.every((g) => inRange(g.thrower) && g.path.length % 3 === 0)) throw new ReplayFormatError("A grenade is not valid.");
  const weaponCount = header.weapons.length;
  if (header.inventory && !header.inventory.every(([, p, ...w]) => p < players && w.every((x) => x < weaponCount))) throw new ReplayFormatError("An inventory is not valid.");
  if (header.economy && !header.economy.every((e) => e.player < players && e.round < header.rounds.length && e.buys.every((x) => x < weaponCount)))
    throw new ReplayFormatError("A round's economy is not valid.");
  const { columns: _c, ...rest } = header;
  return { ...rest, frames, columns: columns as ReplayColumns };
}

/** What the API keeps about a replay, read from its header. */
export function replaySummary(r: Pick<Replay, "map" | "teams" | "players" | "rounds" | "tickRate" | "demoTicks">) {
  const last = r.rounds.at(-1);
  return {
    map: r.map,
    rounds: r.rounds.length,
    score: last ? last.score : ([r.teams[0].score, r.teams[1].score] as [number, number]),
    durationSeconds: Math.round(r.demoTicks / r.tickRate),
  };
}

/** The frame index at or just before a tick, inside [from, to). */
export function frameAtTick(frameTicks: Int32Array, from: number, to: number, tick: number): number {
  let lo = from;
  let hi = to - 1;
  if (hi < lo) return from;
  if (tick <= frameTicks[lo]) return lo;
  if (tick >= frameTicks[hi]) return hi;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (frameTicks[mid] <= tick) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** View yaw from its byte, in degrees (0 = east, counter-clockwise, as in the game). */
export const yawDegrees = (b: number) => (b * 360) / 256;

/**
 * A bot of the game (one standing in for a player who left): it has no Steam ID, the
 * replay names it `bot:<name>`. Bots play on the map but are left out of the scores.
 */
export const isBotId = (steamId: string) => steamId.startsWith("bot:") || steamId === "0" || steamId === "";

/** A team's display name: its clan name, or "Team <first player>". */
export function teamName(r: Pick<Replay, "teams" | "players">, team: 0 | 1): string {
  const clan = r.teams[team].name;
  if (clan) return clan;
  const first = r.players.find((p) => p.team === team && !isBotId(p.steamId));
  return first ? `Team ${first.name}` : team === 0 ? "Team A" : "Team B";
}

/** Each player's kills, deaths, assists and damage to enemies over the whole replay, by player index. */
export function replayTotals(r: Pick<Replay, "players" | "kills" | "hurts">): { kills: number; deaths: number; assists: number; damage: number }[] {
  const rows = r.players.map(() => ({ kills: 0, deaths: 0, assists: 0, damage: 0 }));
  const enemies = (a: number, b: number) => a >= 0 && b >= 0 && a !== b && r.players[a].team !== r.players[b].team;
  for (const k of r.kills) {
    if (k.killer >= 0 && (k.victim < 0 || enemies(k.killer, k.victim))) rows[k.killer].kills++;
    if (k.victim >= 0) rows[k.victim].deaths++;
    if (k.assister >= 0 && rows[k.assister]) rows[k.assister].assists++;
  }
  for (const [, by, to, hp] of r.hurts) if (enemies(by, to)) rows[by].damage += hp;
  return rows;
}
